import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import Prismic from "@prismicio/client";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import "../Css/work.css";
import line from "../Assets/grad_line.svg";
import Lottie from "lottie-react";
import { Link } from "react-router-dom";
import "../Css/caseStudy.css";
import MobileMenu from "./menu";
import arrow from "../Assets/arrow23.svg";
import NDA from "../Assets/NDA.json";
import Contact from "./contact";
import Footer from "./footer";
import "../Css/about.css";
import SEO from "./SEO";

// A single slot-machine reel: a column of 0-9 stacked vertically that spins
// past a couple of full loops before landing on `digit`. Always positioned
// at the correct resting digit when not animating, so worst case (observer
// never fires) it just shows the right number statically — never blank.
function JackpotDigit({ digit, animate, delayMs }) {
  const targetIndex = Number(digit);

  const sequence = useMemo(() => {
    const seq = [];
    const loops = 2;
    for (let l = 0; l < loops; l += 1) {
      for (let d = 0; d <= 9; d += 1) seq.push(d);
    }
    seq.push(targetIndex);
    return seq;
  }, [targetIndex]);

  const restPosition = targetIndex;
  const landedPosition = sequence.length - 1;
  const position = animate ? landedPosition : restPosition;

  return (
    <span className="jackpot-digit">
      <span
        className="jackpot-digit-strip"
        style={{
          // em, not %: percentages in translateY() resolve against this
          // element's own (21-cell-tall) bounding box, not a single cell,
          // so "%" would wildly overshoot. Each cell is exactly 1em tall.
          transform: `translateY(-${position}em)`,
          transitionDelay: animate ? `${delayMs}ms` : "0ms",
        }}
      >
        {sequence.map((d, i) => (
          <span className="jackpot-digit-cell" key={i}>
            {d}
          </span>
        ))}
      </span>
    </span>
  );
}

// Parses a metric like "35%", "5,000+", "$1.2M", "24/7" into
// prefix/number/suffix by finding the first digit run anywhere in the
// string — everything before it is prefix, everything after (even if it
// contains more digits, like the "/7" in "24/7") is suffix. Only that
// first number spins; comma/decimal characters inside it stay static.
function AnimatedMetricValue({ value, animate }) {
  const parsed = useMemo(() => {
    if (typeof value !== "string") return null;
    const match = value.match(/[\d,.]*\d/);
    if (!match) return null;
    const numberPart = match[0];
    const prefix = value.slice(0, match.index);
    const suffix = value.slice(match.index + numberPart.length);
    return { prefix, numberPart, suffix };
  }, [value]);

  if (!parsed) {
    return <>{value}</>;
  }

  const { prefix, numberPart, suffix } = parsed;
  let digitCount = 0;

  return (
    <span className="jackpot-value">
      {prefix && <span className="jackpot-static">{prefix}</span>}
      {numberPart.split("").map((char, i) => {
        if (!/\d/.test(char)) {
          return (
            <span className="jackpot-static" key={i}>
              {char}
            </span>
          );
        }
        const delayMs = digitCount * 60;
        digitCount += 1;
        return (
          <JackpotDigit key={i} digit={char} animate={animate} delayMs={delayMs} />
        );
      })}
      {suffix && <span className="jackpot-static">{suffix}</span>}
    </span>
  );
}

// Same option-card pattern used in MainWork.jsx/OtherWork.jsx's multi-case
// picker modal — duplicated here per this codebase's convention of not
// sharing small components across files.
function MultiCaseOptionCard({ link, fallbackUrl, title, description, image, onNavigate }) {
  const isDocument = link?.link_type === "Document" && link?.uid;
  if (!isDocument && !fallbackUrl) return null;

  const cardContent = (
    <>
      <img className="multi-case-card-image" src={image} alt={title} />
      <div className="multi-case-card-footer">
        <div className="multi-case-card-tag">
          <span className="multi-case-card-dot" />
          <span>{title}</span>
        </div>
        <h3 className="multi-case-card-heading">{description}</h3>
      </div>
    </>
  );

  return isDocument ? (
    <Link to={`/case-study/${link.uid}`} className="multi-case-card" onClick={onNavigate}>
      {cardContent}
    </Link>
  ) : (
    <a
      href={fallbackUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="multi-case-card"
      onClick={onNavigate}
    >
      {cardContent}
    </a>
  );
}

// A single "view another case study" card, horizontal (image left, text +
// year + arrow right). Branches exactly like a MainWork ("worklist") or
// OtherWork ("morework") card would — NDA (static), external link,
// multi-case-study (opens the picker modal), or a direct case-study link —
// since `source` distinguishes which of the two schemas' field names apply.
function OtherCaseStudyCard({ picked, galleryImages, onOpenMultiModal }) {
  const [isHovering, setIsHovering] = useState(false);

  if (!picked) return null;
  const { source, item: e } = picked;
  const isMain = source === "main";

  const title = e?.data?.title;
  const image = e?.data?.images?.[0]?.image_link;
  // Prefer the linked case study's own gallery (header + content images) —
  // the worklist/morework item itself usually only has one thumbnail.
  // Falls back to that single thumbnail while the gallery is still loading.
  const peekImages =
    galleryImages && galleryImages.length > 0
      ? galleryImages
      : (e?.data?.images || [])
          .map((img) => img?.image_link)
          .filter(Boolean)
          .slice(0, 3);
  const description = isMain
    ? e?.data?.long_description
    : e?.data?.small_description;
  const year = isMain ? "2024" : e?.data?.date;

  const cardInner = (
    <>
      <div className="cs-other-card-image">
        <img src={image} alt={title} />
      </div>
      <div className="cs-other-card-content">
        <div className="cs-other-card-text">
          <h2 className="cs-other-card-title">{title}</h2>
          {description && <p className="cs-other-card-desc">{description}</p>}
        </div>
        <div className="cs-other-card-meta">
          {year && <span className="cs-other-card-year">{year}</span>}
          <span className="cs-other-card-arrow">
            <svg
              className="cs-other-card-arrow-icon"
              viewBox="0 0 7 5"
              shapeRendering="crispEdges"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <rect x="3" y="0" width="1" height="1" fill="currentColor" />
              <rect x="4" y="1" width="1" height="1" fill="currentColor" />
              <rect x="0" y="2" width="7" height="1" fill="currentColor" />
              <rect x="4" y="3" width="1" height="1" fill="currentColor" />
              <rect x="3" y="4" width="1" height="1" fill="currentColor" />
            </svg>
          </span>
        </div>
      </div>
    </>
  );

  // A fanned stack of up to 3 images from the project, fixed to the
  // viewport's bottom edge and resting exactly half-visible/half-cut-off —
  // peeks up into view on hover, like photos poking up from below the fold.
  const hoverHandlers = {
    onMouseEnter: () => setIsHovering(true),
    onMouseLeave: () => setIsHovering(false),
  };
  const peek = peekImages.length > 0 && (
    <div
      className={`cs-other-card-peek ${isHovering ? "visible" : ""}`}
      aria-hidden="true"
    >
      {peekImages.map((src, i) => (
        <div
          className={`cs-other-card-peek-item cs-other-card-peek-item-${i}`}
          key={src}
          style={{ transitionDelay: isHovering ? `${i * 70}ms` : "0ms" }}
        >
          <img src={src} alt="" />
        </div>
      ))}
    </div>
  );

  if (e?.data?.nda) {
    return (
      <>
        <div
          className="cs-other-card cs-other-card-static"
          {...hoverHandlers}
        >
          {cardInner}
        </div>
        {peek}
      </>
    );
  }

  if (isMain && e?.data?.onclicklink) {
    return (
      <>
        <a
          href={e?.data?.link}
          target="_blank"
          rel="noopener noreferrer"
          className="cs-other-card"
          {...hoverHandlers}
        >
          {cardInner}
        </a>
        {peek}
      </>
    );
  }

  const multi = isMain
    ? e?.data?.multi_case_studies?.[0]
    : e?.data?.multi_case_study?.[0];
  const isMultiEnabled = isMain ? multi?.multi_case_studies1 : multi?.enable;

  if (isMultiEnabled) {
    return (
      <>
        <div
          role="button"
          tabIndex={0}
          className="cs-other-card"
          onClick={onOpenMultiModal}
          onKeyDown={(evt) => {
            if (evt.key === "Enter" || evt.key === " ") onOpenMultiModal();
          }}
          {...hoverHandlers}
        >
          {cardInner}
        </div>
        {peek}
      </>
    );
  }

  const uid = e?.data?.content_relationship?.uid;
  if (!uid) return null;

  return (
    <>
      <Link to={`/case-study/${uid}`} className="cs-other-card" {...hoverHandlers}>
        {cardInner}
      </Link>
      {peek}
    </>
  );
}

// A small pixel-art padlock (same blocky <rect>-grid technique as the
// arrow icon below) shown in front of NDA/locked entries in the drawer.
function PixelLockIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 7 8"
      shapeRendering="crispEdges"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="1" y="0" width="5" height="1" fill="currentColor" />
      <rect x="0" y="1" width="1" height="1" fill="currentColor" />
      <rect x="6" y="1" width="1" height="1" fill="currentColor" />
      <rect x="0" y="2" width="1" height="1" fill="currentColor" />
      <rect x="6" y="2" width="1" height="1" fill="currentColor" />
      <rect x="0" y="3" width="7" height="1" fill="currentColor" />
      <rect x="0" y="4" width="7" height="1" fill="currentColor" />
      <rect x="0" y="5" width="7" height="1" fill="currentColor" />
      <rect x="0" y="6" width="3" height="1" fill="currentColor" />
      <rect x="4" y="6" width="3" height="1" fill="currentColor" />
      <rect x="0" y="7" width="7" height="1" fill="currentColor" />
    </svg>
  );
}

// One row in the "browse all case studies" drawer — same link branching as
// OtherCaseStudyCard (external link, multi-case-study picker, or a direct
// case-study link) but as a plain text row with a thumbnail that reveals on
// hover instead of a full card. NDA entries are included (unlike the
// random-pick pool) but rendered as a non-clickable locked row instead.
function CaseStudyDrawerItem({ entry, onOpenMultiModal, onNavigate }) {
  const { source, item: e } = entry;
  const isMain = source === "main";
  const title = e?.data?.title;
  const thumb = e?.data?.images?.[0]?.image_link;
  const isLocked = Boolean(e?.data?.nda);

  const rowContent = (
    <>
      {thumb && (
        <span className="cs-drawer-item-thumb" aria-hidden="true">
          <img src={thumb} alt="" />
        </span>
      )}
      <span className="cs-drawer-item-title">{title}</span>
      {isLocked ? (
        <PixelLockIcon className="cs-drawer-item-lock" />
      ) : (
        <svg
          className="cs-other-card-arrow-icon cs-drawer-item-arrow"
          viewBox="0 0 7 5"
          shapeRendering="crispEdges"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <rect x="3" y="0" width="1" height="1" fill="currentColor" />
          <rect x="4" y="1" width="1" height="1" fill="currentColor" />
          <rect x="0" y="2" width="7" height="1" fill="currentColor" />
          <rect x="4" y="3" width="1" height="1" fill="currentColor" />
          <rect x="3" y="4" width="1" height="1" fill="currentColor" />
        </svg>
      )}
    </>
  );

  if (isLocked) {
    return (
      <div className="cs-drawer-item cs-drawer-item-static" aria-disabled="true">
        {rowContent}
      </div>
    );
  }

  if (isMain && e?.data?.onclicklink) {
    return (
      <a
        href={e?.data?.link}
        target="_blank"
        rel="noopener noreferrer"
        className="cs-drawer-item"
      >
        {rowContent}
      </a>
    );
  }

  const multi = isMain
    ? e?.data?.multi_case_studies?.[0]
    : e?.data?.multi_case_study?.[0];
  const isMultiEnabled = isMain ? multi?.multi_case_studies1 : multi?.enable;

  if (isMultiEnabled) {
    return (
      <button
        type="button"
        className="cs-drawer-item"
        onClick={() => onOpenMultiModal(entry)}
      >
        {rowContent}
      </button>
    );
  }

  const uid = e?.data?.content_relationship?.uid;
  if (!uid) return null;

  return (
    <Link
      to={`/case-study/${uid}`}
      className="cs-drawer-item"
      onClick={onNavigate}
    >
      {rowContent}
    </Link>
  );
}

// Prismic's plain-text fields come back as a single string, so a blank line
// the author typed to separate paragraphs is just "\n\n" — split on that for
// paragraphs, then on single "\n" for in-paragraph line breaks, rather than
// making the author pick a separate content type for multi-paragraph copy.
// Lets an author wrap a phrase in a small custom tag to highlight it inline,
// without needing a separate content type just for one styled word. Both
// track this case study's theme colors (--case-green / --case-quotation),
// so a custom Prismic color on the case study affects these too:
//   <g>Cognitive Load:</g> Users need to...   -> --case-green
//   <r>Another Heading:</r> Some more text... -> --case-quotation
const INLINE_TAG_CLASSES = { g: "green", r: "case-accent-quotation" };
const INLINE_TAG_PATTERN = /<(g|r)>(.*?)<\/\1>/g;

function renderInlineTags(line) {
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  INLINE_TAG_PATTERN.lastIndex = 0;
  while ((match = INLINE_TAG_PATTERN.exec(line)) !== null) {
    if (match.index > lastIndex) {
      parts.push(line.slice(lastIndex, match.index));
    }
    parts.push(
      <span className={INLINE_TAG_CLASSES[match[1]]} key={key++}>
        {match[2]}
      </span>,
    );
    lastIndex = INLINE_TAG_PATTERN.lastIndex;
  }
  if (lastIndex < line.length) parts.push(line.slice(lastIndex));

  return parts;
}

// The "Code" content type's single field doubles as either a Figma
// prototype link (embedded as an iframe) or literal text/code (rendered as
// a preformatted block) — whichever the author actually typed into it.
const FIGMA_LINK_PATTERN = /^https?:\/\/([\w-]+\.)?figma\.com\//i;
function isFigmaLink(value) {
  return typeof value === "string" && FIGMA_LINK_PATTERN.test(value.trim());
}

// A paragraph starting with "1.", "2)", "-", or "•" is treated as a list
// item: the marker is stripped and replaced with the same bullet-dot
// styling the Points-Text content type uses, so a plain numbered/bulleted
// list typed straight into a text field renders as a real list instead of
// a paragraph that starts with a stray "1.".
const BULLET_LINE_PATTERN = /^\s*(?:[-•*]|\d+[.)])\s+([\s\S]*)$/;

function renderMultilineText(text, className) {
  if (!text) return null;
  return text
    .split(/\n{2,}/)
    .filter((paragraph) => paragraph.trim() !== "")
    .map((paragraph, pIndex) => {
      const bulletMatch = paragraph.match(BULLET_LINE_PATTERN);
      const body = bulletMatch ? bulletMatch[1] : paragraph;
      const lines = body.split("\n").map((line, lIndex, arr) => (
        <React.Fragment key={lIndex}>
          {renderInlineTags(line)}
          {lIndex < arr.length - 1 && <br />}
        </React.Fragment>
      ));

      return bulletMatch ? (
        <p className={`${className} points-line`} key={pIndex}>
          <span className="bullet-dot"></span>
          {lines}
        </p>
      ) : (
        <p className={className} key={pIndex}>
          {lines}
        </p>
      );
    });
}

export default function CaseStudy({ caseStudy, isProtected, isLoading, login }) {
  const [showSideNav, setShowSideNav] = useState(false);
  // The bottom scrubber + back-to-top box only make sense once there's a
  // "Role and Contributions" section worth of content to scrub through, and
  // should get out of the way again once the reader reaches Thank You.
  const [showBottomBar, setShowBottomBar] = useState(false);
  const headerImageRef = useRef(null);
  const thankYouRef = useRef(null);
  const contentSectionRef = useRef(null);

  // Metrics count up the first time the metrics section scrolls into view.
  const metricsSectionRef = useRef(null);
  const [metricsInView, setMetricsInView] = useState(false);

  useEffect(() => {
    const handleSideNavScroll = () => {
      if (headerImageRef.current) {
        const headerRect = headerImageRef.current.getBoundingClientRect();
        const pastHeader = headerRect.bottom < 0;

        let beforeThankYou = true;
        let reachedThankYou = false;
        if (thankYouRef.current) {
          const thankYouRect = thankYouRef.current.getBoundingClientRect();
          beforeThankYou = thankYouRect.top > window.innerHeight * 0.7;
          reachedThankYou = thankYouRect.top < window.innerHeight * 0.3;
        }

        let reachedContent = false;
        if (contentSectionRef.current) {
          const contentRect =
            contentSectionRef.current.getBoundingClientRect();
          reachedContent = contentRect.top < window.innerHeight * 0.85;
        }

        if (metricsSectionRef.current) {
          const metricsRect = metricsSectionRef.current.getBoundingClientRect();
          // Re-triggers on every fresh entry, from either direction: top
          // edge crossing down into view (scrolling down) or bottom edge
          // still below the viewport's top (scrolling back up into it).
          const metricsInViewNow =
            metricsRect.top < window.innerHeight * 0.9 &&
            metricsRect.bottom > 0;
          setMetricsInView(metricsInViewNow);
        }

        setShowSideNav(pastHeader && beforeThankYou);
        setShowBottomBar(reachedContent && !reachedThankYou);
      }
    };

    handleSideNavScroll();
    window.addEventListener("scroll", handleSideNavScroll);
    return () => window.removeEventListener("scroll", handleSideNavScroll);
  }, []);

  const [activeSection, setActiveSection] = useState(null);

  // Mobile-only bottom scrubber: tracks how far down the page has been
  // scrolled (0-1) so a thumb can slide across a fixed row of tick marks —
  // one tick per section, matching the desktop side menu's outline.
  const [scrollProgress, setScrollProgress] = useState(0);
  const scrollProgressTrackRef = useRef(null);
  const isScrollDraggingRef = useRef(false);
  const caseStudyTilesCount = caseStudy?.tiles?.length;
  const scrollProgressTicks = useMemo(() => {
    const count = caseStudyTilesCount || 8;
    return Array.from({ length: count }, (_, i) => i);
  }, [caseStudyTilesCount]);

  useEffect(() => {
    let ticking = false;

    const updateScrollProgress = () => {
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollable > 0 ? window.scrollY / scrollable : 0;
      setScrollProgress(Math.min(1, Math.max(0, progress)));
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollProgress);
        ticking = true;
      }
    };

    updateScrollProgress();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Dragging the track scrubs the page: convert the pointer's x position
  // within the track into a scroll target.
  const scrubToClientX = (clientX) => {
    const track = scrollProgressTrackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(
      1,
      Math.max(0, (clientX - rect.left) / rect.width),
    );
    const scrollable =
      document.documentElement.scrollHeight - window.innerHeight;
    window.scrollTo({ top: ratio * scrollable });
    setScrollProgress(ratio);
  };

  const handleScrollTrackPointerDown = (e) => {
    isScrollDraggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    scrubToClientX(e.clientX);
  };

  const handleScrollTrackPointerMove = (e) => {
    if (!isScrollDraggingRef.current) return;
    scrubToClientX(e.clientX);
  };

  const handleScrollTrackPointerUp = (e) => {
    isScrollDraggingRef.current = false;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // "View another case study" card: pulls from both the home page's
  // worklist ("Case Studies with Results") and morework ("Client
  // Projects") lists, same as MainWork.jsx/OtherWork.jsx.
  const [otherWorklistDocs, setOtherWorklistDocs] = useState(null);
  const [otherMoreworkDocs, setOtherMoreworkDocs] = useState(null);

  useEffect(() => {
    const apiEndpoint = "https://harshitsharma.prismic.io/api/v2";
    const accessToken =
      "MC5ZYUVtbXhNQUFETUFuODZN.77-977-9Ye-_ve-_vX8o77-977-977-977-977-9a--_ve-_ve-_ve-_ve-_ve-_vQITBF5h77-977-977-977-977-977-9TO-_vQ";
    const client = Prismic.client(apiEndpoint, { accessToken });

    const fetchOtherCaseStudies = async () => {
      const [worklistRes, moreworkRes] = await Promise.all([
        client.query(Prismic.Predicates.at("document.type", "worklist"), {
          orderings: "[my.worklist.order desc]",
        }),
        client.query(Prismic.Predicates.at("document.type", "morework"), {
          orderings: "[my.morework.order desc]",
        }),
      ]);
      if (worklistRes) setOtherWorklistDocs(worklistRes.results);
      if (moreworkRes) setOtherMoreworkDocs(moreworkRes.results);
    };

    fetchOtherCaseStudies();
  }, []);

  // The current case study's uid, derived from the route the same way
  // caseStudyMain.jsx does, so it can be excluded from the "other case
  // study" pool below.
  const currentCaseStudyUid = useMemo(() => {
    const path = location.pathname;
    return path.substring(path.lastIndexOf("/") + 1);
  }, [location.pathname]);

  const eligibleOtherCaseStudies = useMemo(() => {
    const main = (otherWorklistDocs || []).map((item) => ({
      source: "main",
      item,
    }));
    const more = (otherMoreworkDocs || []).map((item) => ({
      source: "more",
      item,
    }));
    return [...main, ...more].filter(({ source, item }) => {
      if (item?.data?.nda) return false;

      const uid = item?.data?.content_relationship?.uid;
      if (uid) return uid !== currentCaseStudyUid;

      // No direct link (some Prismic entries have an unset/broken
      // content_relationship — {link_type:"Document"} with no uid). Only
      // still eligible if it resolves another way, or OtherCaseStudyCard
      // would render nothing for it.
      const isMain = source === "main";
      if (isMain && item?.data?.onclicklink) return true;
      const multi = isMain
        ? item?.data?.multi_case_studies?.[0]
        : item?.data?.multi_case_study?.[0];
      return Boolean(isMain ? multi?.multi_case_studies1 : multi?.enable);
    });
  }, [otherWorklistDocs, otherMoreworkDocs, currentCaseStudyUid]);

  // Same pool as above, but for the "browse all case studies" drawer, which
  // — unlike the single random-pick card — should still list NDA/locked
  // case studies (just shown with a lock icon and not clickable) instead of
  // hiding them entirely.
  const allCaseStudiesForDrawer = useMemo(() => {
    const main = (otherWorklistDocs || []).map((item) => ({
      source: "main",
      item,
    }));
    const more = (otherMoreworkDocs || []).map((item) => ({
      source: "more",
      item,
    }));
    return [...main, ...more].filter(({ source, item }) => {
      if (item?.data?.nda) return true;

      const uid = item?.data?.content_relationship?.uid;
      if (uid) return uid !== currentCaseStudyUid;

      const isMain = source === "main";
      if (isMain && item?.data?.onclicklink) return true;
      const multi = isMain
        ? item?.data?.multi_case_studies?.[0]
        : item?.data?.multi_case_study?.[0];
      return Boolean(isMain ? multi?.multi_case_studies1 : multi?.enable);
    });
  }, [otherWorklistDocs, otherMoreworkDocs, currentCaseStudyUid]);

  // Picked once per case study (not on every render, which would make the
  // card change out from under the reader) — keyed on the current uid too,
  // not just the pool's length, since navigating to a different case study
  // usually leaves the pool the same size (still N-1 after excluding the
  // new current one) and would otherwise never re-pick.
  const otherCaseStudyPick = useMemo(() => {
    if (!eligibleOtherCaseStudies.length) return null;
    const index = Math.floor(Math.random() * eligibleOtherCaseStudies.length);
    return eligibleOtherCaseStudies[index];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleOtherCaseStudies.length, currentCaseStudyUid]);

  // The worklist/morework item itself usually only has one thumbnail — the
  // real gallery lives on the linked case study's own page (header image +
  // every "Image" content tile). Fetch that full document so the hover
  // peek has real project screenshots to fan out, not just one.
  const [otherCaseStudyImages, setOtherCaseStudyImages] = useState([]);

  useEffect(() => {
    const pickedUid = otherCaseStudyPick?.item?.data?.content_relationship?.uid;
    if (!pickedUid) {
      setOtherCaseStudyImages([]);
      return;
    }

    let cancelled = false;
    const apiEndpoint = "https://harshitsharma.prismic.io/api/v2";
    const accessToken =
      "MC5ZYUVtbXhNQUFETUFuODZN.77-977-9Ye-_ve-_vX8o77-977-977-977-977-9a--_ve-_ve-_ve-_ve-_ve-_vQITBF5h77-977-977-977-977-977-9TO-_vQ";
    const client = Prismic.client(apiEndpoint, { accessToken });

    client.getByUID("case_study", pickedUid).then((response) => {
      if (cancelled || !response) return;
      const data = response.data;
      const tileImages = (data?.tiles || [])
        .filter((t) => t?.content_type === "Image" && t?.image_link)
        .map((t) => t.image_link);
      const images = [data?.header_image, ...tileImages].filter(Boolean);
      setOtherCaseStudyImages([...new Set(images)].slice(0, 6));
    });

    return () => {
      cancelled = true;
    };
  }, [otherCaseStudyPick]);

  // Which {source, item} pair's multi-case-study picker modal is open, if
  // any — generalized from a single boolean so both the "View Next Case
  // Study" card AND any row in the "browse all" drawer below can trigger
  // this same modal for whichever item they represent.
  const [multiModalItem, setMultiModalItem] = useState(null);
  const [isCaseStudyDrawerOpen, setIsCaseStudyDrawerOpen] = useState(false);

  useEffect(() => {
    const shouldLock = Boolean(multiModalItem) || isCaseStudyDrawerOpen;
    document.body.style.overflow = shouldLock ? "hidden" : "";
    document.documentElement.style.overflow = shouldLock ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [multiModalItem, isCaseStudyDrawerOpen]);

  useEffect(() => {
    if (!isCaseStudyDrawerOpen) return;
    const onKeyDown = (evt) => {
      if (evt.key === "Escape") setIsCaseStudyDrawerOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isCaseStudyDrawerOpen]);

  useEffect(() => {
    // Don't lock scroll while still loading — isProtected defaults to
    // false until the fetch resolves, but that's not a real "gated"
    // state yet, just data not being in hand.
    const showingGate = !isLoading && !isProtected;
    document.body.style.overflow = showingGate ? "hidden" : "";
    document.documentElement.style.overflow = showingGate ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isProtected, isLoading]);

  useEffect(() => {
    if (!isLoading && caseStudy.tiles && caseStudy.tiles.length > 0) {
      const sections = document.querySelectorAll(".case-study");

      if (sections.length === 0) {
        console.error("No sections found for observation.");
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const sectionId = entry.target.id;
            const sectionData = caseStudy.tiles?.find(
              (tile, index) => `section-${index}` === sectionId,
            );

            if (entry.isIntersecting && sectionData?.heading) {
              setActiveSection(entry.target.id);
            }
          });
        },
        {
          root: null,
          threshold: [0.1, 0.8],
        },
      );

      sections.forEach((section) => observer.observe(section));

      return () => {
        sections.forEach((section) => observer.unobserve(section));
      };
    }
  }, [isLoading, caseStudy.tiles]);

  const buildRenderTiles = (tiles) => {
    const result = [];
    let i = 0;

    const isSplitImage = (t) =>
      t?.content_type === "Image" && t?.container_size === "split";

    const isPointsText = (t) => t?.content_type === "Points-Text";

    while (i < (tiles?.length || 0)) {
      const current = tiles[i];
      const next = tiles[i + 1];
      const nextNext = tiles[i + 2];

      if (isSplitImage(current) && isSplitImage(next)) {
        result.push({
          type: "split-pair",
          first: current,
          second: next,
          originalIndex: i,
        });
        i += 2;
        continue;
      }

      if (current.content_type === "Heading-only") {
        if (isSplitImage(next) && isSplitImage(nextNext)) {
          result.push({
            type: "heading-split-pair",
            heading: current.heading,
            first: next,
            second: nextNext,
            originalIndex: i,
          });
          i += 3;
          continue;
        }

        if (isPointsText(next)) {
          const items = [];
          let j = i + 1;
          while (j < tiles.length && isPointsText(tiles[j])) {
            items.push(tiles[j]);
            j++;
          }
          result.push({
            type: "points-group",
            heading: current.heading,
            items,
            originalIndex: i,
          });
          i = j;
          continue;
        }

        if (next) {
          result.push({
            pairedHeading: current.heading,
            contentTile: next,
            originalIndex: i,
          });
          i += 2;
          continue;
        }
      }

      result.push({ tile: current, originalIndex: i });
      i += 1;
    }

    return result;
  };

  const scrollToSection = (index) => {
    const section = document.getElementById(`section-${index}`);
    if (section) {
      const offset = window.innerHeight / 4 - section.offsetHeight / 2;
      const sectionTop =
        section.getBoundingClientRect().top + window.pageYOffset;

      window.scrollTo({
        top: sectionTop - offset,
        behavior: "smooth",
      });
    }
  };

  const renderedBlocks = buildRenderTiles(caseStudy.tiles);

  const hasMetrics = caseStudy.metrics?.some(
    (m) => m.value_1 && m.value_1.trim() !== "",
  );

  const metricsOrder = caseStudy.metrics?.[0]?.order;

  const metricsInsertIndex =
    metricsOrder !== undefined && metricsOrder !== null && metricsOrder !== ""
      ? Math.max(0, Math.min(Number(metricsOrder), renderedBlocks.length))
      : renderedBlocks.length - 2;

  const metricsSection = hasMetrics && (
    <section
      className="case-study"
      key="metrics-section"
      ref={metricsSectionRef}
    >
      <div className="description-CS">
        <h1 className="metrics-heading">
          {caseStudy.metrics[0]?.metrics_heading || "Metrics"}
        </h1>
        <div className="metrics-grid">
          {caseStudy.metrics
            .filter((m) => m.value_1 && m.value_1.trim() !== "")
            .map((m, i) => (
              <div className="metric-box" key={i}>
                <div className="metric-divider">
                  <span className="metric-value">
                    <AnimatedMetricValue
                      value={m.value_1}
                      animate={metricsInView}
                    />
                  </span>
                  {m.label && <span className="metric-label">{m.label}</span>}
                </div>
                {m.description1 && (
                  <p className="metric-description">{m.description1}</p>
                )}
              </div>
            ))}
        </div>
      </div>
    </section>
  );

  const themeColors = caseStudy.color?.[0];

 const themeStyle = {
  "--bg": themeColors?.bg || "#010510", // custom from Prismic, falls back to your original default
  "--grid-line": themeColors?.border || "#07245a",
  "--case-green": themeColors?.green_heading || "#3075ff",
  "--bg-light": themeColors?.bg_light || "#00153D",
  "--case-quotation": themeColors?.quotation || "#69ffca",
  "--case-heading-points": themeColors?.heading_points || "#e7ff7c",
};


  return (
    <div style={themeStyle}>
      <div className="caseStudy-container" >
        {!isLoading && caseStudy.title && (
          <SEO
            title={caseStudy.description || caseStudy.title}
            description={
              caseStudy.key_fact ||
              `${caseStudy.title} — a UX/UI product design case study by Harshit Sharma, Lead Product Designer.`
            }
            path={`/case-study/${currentCaseStudyUid}`}
            image={caseStudy.header_image}
            noIndex={!isProtected}
          />
        )}
        <MobileMenu />

        {/* Render the main (skeleton-holding) branch while still loading
            too — isProtected defaults to false until the fetch resolves,
            so gating purely on it means this branch (and every skeleton
            in it) would never get a chance to paint during the actual
            loading window. Only fall back to the password gate once
            loading has actually finished and it's confirmed locked. */}
        {isLoading || isProtected ? (
          <>
            <div className="case-study-layout">
              <div className={`menu-test ${showSideNav ? "visible" : ""}`}>
                <p>Menu</p>
                <ul>
                  {caseStudy.tiles?.map((tile, index) => (
                    <li key={`menu-item-${index}`}>
                      <a
                        href={`#section-${index}`}
                        className={`menu-link ${
                          activeSection === `section-${index}` ? "active" : ""
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          scrollToSection(index);
                        }}
                      >
                        {tile.heading}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="case-study-main">
                <div className="cs-header">
                  <div className="cs-header-left">
                    <Link to="/" className="cs-back-btn">
                      ← BACK
                    </Link>

                    <div className="cs-header-content-bottom">
                      <div className="cs-project-tag">
                        <div className="cs-project-logo">
                          {isLoading ? (
                            <div
                              className="skeleton skeleton-circle"
                              style={{ width: "100%", height: "100%" }}
                            />
                          ) : (
                            <img src={caseStudy.logo} alt={caseStudy.title} />
                          )}
                        </div>
                        <span>
                          {isLoading ? (
                            <span
                              className="skeleton skeleton-text"
                              style={{ width: 80 }}
                            />
                          ) : (
                            caseStudy.title
                          )}
                        </span>
                      </div>

                      <h1 className="cs-heading">
                        {isLoading ? (
                          <span
                            className="skeleton skeleton-text"
                            style={{ width: "70%", height: "1em" }}
                          />
                        ) : (
                          caseStudy.description
                        )}
                      </h1>
                    </div>
                  </div>

                  <div className="cs-header-right">
                    <div className="cs-detail-cell">
                      <div className="cs-detail-icon-label">
                        <span className="cs-detail-icon">
                          <span
                            className="cs-detail-icon-mask"
                            style={{
                              WebkitMaskImage:
                                "url(https://ik.imagekit.io/harshitsharma/company_logos/role_-1RSs9zHx.svg)",
                              maskImage:
                                "url(https://ik.imagekit.io/harshitsharma/company_logos/role_-1RSs9zHx.svg)",
                            }}
                            role="img"
                            aria-label="Role icon"
                          />
                        </span>
                        <span className="cs-detail-label">ROLE</span>
                      </div>
                      <span className="cs-detail-value">
                        {isLoading ? (
                          <span
                            className="skeleton skeleton-text"
                            style={{ width: 90 }}
                          />
                        ) : (
                          caseStudy.role
                        )}
                      </span>
                    </div>

                    <div className="cs-detail-cell">
                      <div className="cs-detail-icon-label">
                        <span className="cs-detail-icon">
                          <span
                            className="cs-detail-icon-mask"
                            style={{
                              WebkitMaskImage:
                                "url(https://ik.imagekit.io/harshitsharma/company_logos/team_Vxm_bJCUH.svg?updatedAt=1785781351503)",
                              maskImage:
                                "url(https://ik.imagekit.io/harshitsharma/company_logos/team_Vxm_bJCUH.svg?updatedAt=1785781351503)",
                            }}
                            role="img"
                            aria-label="Team icon"
                          />
                        </span>
                        <span className="cs-detail-label">TEAM</span>
                      </div>
                      <span className="cs-detail-value">
                        {isLoading ? (
                          <span
                            className="skeleton skeleton-text"
                            style={{ width: 90 }}
                          />
                        ) : (
                          caseStudy.teams?.map((t) => t.team_name).join(", ")
                        )}
                      </span>
                    </div>

                    <div className="cs-detail-cell">
                      <div className="cs-detail-icon-label">
                        <span className="cs-detail-icon">
                          <span
                            className="cs-detail-icon-mask"
                            style={{
                              WebkitMaskImage:
                                "url(https://ik.imagekit.io/harshitsharma/company_logos/cal_wIHahbUwg.svg?updatedAt=1785781351495)",
                              maskImage:
                                "url(https://ik.imagekit.io/harshitsharma/company_logos/cal_wIHahbUwg.svg?updatedAt=1785781351495)",
                            }}
                            role="img"
                            aria-label="Duration icon"
                          />
                        </span>
                        <span className="cs-detail-label">DURATION</span>
                      </div>
                      <span className="cs-detail-value">
                        {isLoading ? (
                          <span
                            className="skeleton skeleton-text"
                            style={{ width: 90 }}
                          />
                        ) : (
                          caseStudy.date
                        )}
                      </span>
                    </div>

                    {caseStudy?.launch_project && (
                      <div className="cs-detail-cell">
                        <div className="cs-detail-icon-label">
                          <span className="cs-detail-icon">
                            <span
                              className="cs-detail-icon-mask"
                              style={{
                                WebkitMaskImage:
                                  "url(https://ik.imagekit.io/harshitsharma/company_logos/exp_hOsmG0Q4i.svg?updatedAt=1785781351304)",
                                maskImage:
                                  "url(https://ik.imagekit.io/harshitsharma/company_logos/exp_hOsmG0Q4i.svg?updatedAt=1785781351304)",
                              }}
                              role="img"
                              aria-label="Live project icon"
                            />
                          </span>
                          <span className="cs-detail-label">LIVE PROJECT</span>
                        </div>
                        <a
                          href={caseStudy.launch}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cs-detail-value cs-visit-link"
                        >
                          Visit ↗
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="cs-key-fact-row">
                    <span className="cs-key-fact-tag">✱ KEY FACT</span>
                    <p className="cs-key-fact-text">
                      {isLoading ? (
                        <span
                          className="skeleton skeleton-text"
                          style={{ width: "80%" }}
                        />
                      ) : (
                        caseStudy.key_fact
                      )}
                    </p>
                  </div>
                </div>

                <div className={"case-study full"} ref={headerImageRef}>
                  {isLoading ? (
                    <div
                      className="skeleton"
                      style={{ width: "100%", height: "100%", minHeight: 400 }}
                    />
                  ) : (
                    <img
                      className="img-fluid"
                      src={caseStudy.header_image}
                      alt={caseStudy.title}
                    />
                  )}
                </div>

                <div className="case-study-content" ref={contentSectionRef}>
                  {isLoading ? (
                    <div className="description-tags">
                      <h1>
                        <span
                          className="skeleton skeleton-text"
                          style={{ width: 220, height: "1em" }}
                        />
                      </h1>
                      <div className="case-study-tags">
                        <div className="tags-cs">
                          {[0, 1, 2].map((i) => (
                            <span
                              key={i}
                              className="skeleton"
                              style={{
                                display: "inline-block",
                                width: 70,
                                height: 32,
                                marginRight: 8,
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : caseStudy?.tagss ? (
                    <div className="description-tags">
                      <h1>Role and Contributions</h1>
                      <div className="case-study-tags">
                        <div className="tags-cs">
                          {caseStudy.tags?.map((e) => (
                            <button className={`tag`}>{e.tag}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="case-study-tags"></div>
                  )}

                  {/* API content still loading: caseStudy.tiles is empty
                      so renderedBlocks has nothing to map — show generic
                      block placeholders instead of leaving this blank. */}
                  {isLoading &&
                    [0, 1, 2].map((i) => (
                      <section className="case-study" key={`skeleton-block-${i}`}>
                        <div className="description-CS">
                          <span
                            className="skeleton skeleton-text"
                            style={{
                              width: 200,
                              height: "1.4em",
                              marginBottom: 16,
                              display: "inline-block",
                            }}
                          />
                          <div
                            className="skeleton"
                            style={{ width: "100%", height: 320 }}
                          />
                        </div>
                      </section>
                    ))}

                  {!isLoading && renderedBlocks.map((block, index) => {
                    const shouldInsertMetricsHere =
                      index === metricsInsertIndex;

                    let tileElement;

                    if (block.type === "split-pair") {
                      tileElement = (
                        <section
                          key={index}
                          id={`section-${block.originalIndex}`}
                          className="case-study split-row"
                        >
                          <div className="image-div split">
                            <img
                              className="img-fluid"
                              src={block.first.image_link}
                              alt={block.first.image_desc || ""}
                            />
                            {block.first.image_desc && (
                              <p className="image-description">
                                {block.first.image_desc}
                              </p>
                            )}
                          </div>
                          <div className="image-div split">
                            <img
                              className="img-fluid"
                              src={block.second.image_link}
                              alt={block.second.image_desc || ""}
                            />
                            {block.second.image_desc && (
                              <p className="image-description">
                                {block.second.image_desc}
                              </p>
                            )}
                          </div>
                        </section>
                      );
                    } else if (block.type === "heading-split-pair") {
                      tileElement = (
                        <section
                          key={index}
                          id={`section-${block.originalIndex}`}
                          className="case-study heading-image-stack"
                        > 
                          <h1 className="stack-heading">{block.heading}</h1>
                          <div className="split-row-inline">
                            <div className="image-div split">
                              <img
                                className="img-fluid"
                                src={block.first.image_link}
                                alt={block.first.image_desc || ""}
                              />
                            </div>
                            <div className="image-div split">
                              <img
                                className="img-fluid"
                                src={block.second.image_link}
                                alt={block.second.image_desc || ""}
                              />
                            </div>
                          </div>
                        </section>
                      );
                    } else if (block.type === "points-group") {
                      tileElement = (
                        <section
                          key={index}
                          id={`section-${block.originalIndex}`}
                          className="case-study"
                        >
                          <div className="description-CS">
                            <h1>{block.heading}</h1>
                            <div className="case-study-ng points-group-list">
                              {block.items.map((item, itemIndex) => (
                                <p
                                  className="case-study-text points-line"
                                  key={itemIndex}
                                >
                                  <span className="bullet-dot"></span>
                                  <span className="points-label">
                                    {item.points}
                                  </span>
                                  {" : "}
                                  {item.text_content}
                                </p>
                              ))}
                            </div>
                          </div>
                        </section>
                      );
                    } else if (block.pairedHeading) {
                      const e = block.contentTile;
                      tileElement = (
                        <section
                          key={index}
                          id={`section-${block.originalIndex}`}
                          className={`case-study ${e.container_size}`}
                        >
                          {e.content_type === "Image" ? (
                            <>
                              {isLoading ? (
                                <div
                                  className="skeleton"
                                  style={{ width: "100%", height: 300 }}
                                />
                              ) : (
                                <div
                                  className={`image-div ${e.container_size === "split" ? "split" : "fill"}`}
                                >
                                  <img
                                    className="img-fluid"
                                    src={e.image_link}
                                    alt={e.image_desc || ""}
                                  />
                                  <p className="image-description">
                                    {e.image_desc}
                                  </p>
                                </div>
                              )}
                            </>
                          ) : e.content_type === "Text" ? (
                            <div className="description-CS">
                              <h1>{block.pairedHeading}</h1>
                              <div className="case-study-ng">
                                {renderMultilineText(
                                  e.text_content,
                                  "case-study-text",
                                )}
                              </div>
                            </div>
                          ) : e.content_type === "Heading-text" ? (
                            <div className="description-CS">
                              <h1>{block.pairedHeading}</h1>
                              <div className="case-study-ng">
                                {renderMultilineText(
                                  e.text_content,
                                  "case-study-text",
                                )}
                              </div>
                            </div>
                          ) : e.content_type === "Heading-green" ? (
                            <div className="description-CS">
                              <h1>{block.pairedHeading}</h1>
                              <div className="case-study-ng">
                                {e.heading_green && (
                                  <p className="case-study-text green">
                                    {e.heading_green}
                                  </p>
                                )}
                                {renderMultilineText(
                                  e.text_content,
                                  "case-study-text",
                                )}
                              </div>
                            </div>
                          ) : e.content_type === "Heading-desc" ? (
                            <div className="description-CS">
                              <h1>{block.pairedHeading}</h1>
                              <div className="case-study-ng">
                                {e.heading_green && (
                                  <p className="case-study-text green">
                                    {e.heading_green}
                                  </p>
                                )}
                                {renderMultilineText(
                                  e.text_content,
                                  "case-study-text",
                                )}
                              </div>
                            </div>
                          ) : e.content_type === "Points-Text" ? (
                            <div className="description-CS">
                              <h1>{block.pairedHeading}</h1>
                              <p
                                className="case-study-text points-line"
                                style={{ width: "80%" }}
                              >
                                <span className="bullet-dot"></span>
                                <span className="points-label">{e.points}</span>
                                {" : "}
                                {e.text_content}
                              </p>
                            </div>
                          ) : e.content_type === "Divide" ? (
                            <div className="description-CS">
                              <h1>{block.pairedHeading}</h1>
                              <div className="break">
                                <hr
                                  className="solid"
                                  style={{
                                    opacity: 0.2,
                                    backgroundColor: "#272746",
                                  }}
                                />
                              </div>
                            </div>
                          ) : e.content_type === "Quotation" ? (
                            <div className="description-CS">
                              <h1>{block.pairedHeading}</h1>
                              <div className="case-study-q">
                                <p className="case-study-quoation">
                                  {e.quotation}
                                </p>
                              </div>
                            </div>
                          ) : e.content_type === "Code" ? isFigmaLink(e.code) ? (
                            <div
                              className={`image-div ${e.container_size === "split" ? "split" : "fill"}`}
                            >
                              <div className="figma-embed-wrap">
                                <iframe
                                  className="figma-embed-frame"
                                  src={`https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(e.code)}`}
                                  allowFullScreen
                                  title={e.image_desc || "Figma prototype"}
                                ></iframe>
                              </div>
                              {e.image_desc && (
                                <p className="image-description">
                                  {e.image_desc}
                                </p>
                              )}
                            </div>
                          ) : (
                            e.code && (
                              <div className="description-CS">
                                <h1>{block.pairedHeading}</h1>
                                <pre className="cs-code-block">
                                  <code>{e.code}</code>
                                </pre>
                              </div>
                            )
                          ) : e.content_type === "Video-text" ? (
                            <div className="description-CS">
                              <h1>{block.pairedHeading}</h1>
                              <div className="video-container">
                                <video
                                  className="video-fluid-desc"
                                  src={e.video_link}
                                  autoPlay="autoplay"
                                  loop="loop"
                                ></video>
                                <div className="video-desc">
                                  <h1>{e?.heading_green}</h1>
                                  {e?.text_content && (
                                    <p
                                      className="case-study-text"
                                      style={{ color: "#000000b5" }}
                                    >
                                      {e?.text_content}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="description-CS">
                              <h1>{block.pairedHeading}</h1>
                              <video
                                className="img-fluid"
                                src={e.video_link}
                                autoPlay="autoplay"
                                loop="loop"
                              ></video>
                            </div>
                          )}
                        </section>
                      );
                    } else {
                      const e = block.tile;
                      tileElement = (
                        <section
                          key={index}
                          id={`section-${block.originalIndex}`}
                          className={`case-study ${e.container_size}`}
                        >
                          {e.content_type === "Image" ? (
                            <>
                              {isLoading ? (
                                <div
                                  className="skeleton"
                                  style={{ width: "100%", height: 300 }}
                                />
                              ) : (
                                <div className="image-div">
                                  <img
                                    className="img-fluid"
                                    src={e.image_link}
                                    alt={e.image_desc || ""}
                                  />
                                  <p className="image-description">
                                    {e.image_desc}
                                  </p>
                                </div>
                              )}
                            </>
                          ) : e.content_type === "Text" ? (
                            <div className="description-CS">
                              <h1>{e.heading}</h1>
                              <div className="case-study-ng">
                                {renderMultilineText(
                                  e.text_content,
                                  "case-study-text",
                                )}
                              </div>
                            </div>
                          ) : e.content_type === "Heading-text" ? (
                            <div className="description-CS">
                              <h1>{e.heading}</h1>
                              <div className="case-study-ng">
                                {renderMultilineText(
                                  e.text_content,
                                  "case-study-text",
                                )}
                              </div>
                            </div>
                          ) : e.content_type === "Heading-green" ? (
                            <div className="description-CS">
                              <h1>{e.heading}</h1>
                              <div className="case-study-ng">
                                {e.heading_green && (
                                  <p className="case-study-text green">
                                    {e.heading_green}
                                  </p>
                                )}
                                {renderMultilineText(
                                  e.text_content,
                                  "case-study-text",
                                )}
                              </div>
                            </div>
                          ) : e.content_type === "Block" ? (
                            <div className="description-CS">
                              <div className="block-container">
                                <div className="case-study-ng">
                                  {e.heading_green && (
                                    <p
                                      className="case-study-text green"
                                      style={{ color: "#9CFF8C" }}
                                    >
                                      {e.heading_green}
                                    </p>
                                  )}
                                  {renderMultilineText(
                                    e.text_content,
                                    "case-study-text",
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : e.content_type === "Heading-only" ? (
                            <div className="heading-only-block">
                              <h1>{e.heading}</h1>
                            </div>
                          ) : e.content_type === "Points-Text" ? (
                            <div className="description-CS">
                              <div className="heading-spacer"></div>
                              <div className="case-study-ng">
                                <p className="case-study-text points-line">
                                  <span className="bullet-dot"></span>
                                  <span className="points-label">
                                    {e.points}
                                  </span>
                                  {" : "}
                                  {e.text_content}
                                </p>
                              </div>
                            </div>
                          ) : e.content_type === "Heading-desc" ? (
                            <div className="description-CS">
                              <h1>{e.heading}</h1>
                              <div className="case-study-ng">
                                {e.heading_green && (
                                  <p className="case-study-text green">
                                    {e.heading_green}
                                  </p>
                                )}
                                {renderMultilineText(
                                  e.text_content,
                                  "case-study-text",
                                )}
                              </div>
                            </div>
                          ) : e.content_type === "Divide" ? (
                            <div className="break">
                              <hr
                                className="solid"
                                style={{
                                  opacity: 0.2,
                                  backgroundColor: "#272746",
                                }}
                              />
                            </div>
                          ) : e.content_type === "Quotation" ? (
                            <div className="description-CS">
                              <h1>{e.heading}</h1>
                              <div className="case-study-q">
                                <p className="case-study-quoation">
                                  {e.quotation}
                                </p>
                              </div>
                            </div>
                          ) : e.content_type === "Code" ? isFigmaLink(e.code) ? (
                            <div className="image-div">
                              <div className="figma-embed-wrap">
                                <iframe
                                  className="figma-embed-frame"
                                  src={`https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(e.code)}`}
                                  allowFullScreen
                                  title={e.image_desc || "Figma prototype"}
                                ></iframe>
                              </div>
                              {e.image_desc && (
                                <p className="image-description">
                                  {e.image_desc}
                                </p>
                              )}
                            </div>
                          ) : (
                            e.code && (
                              <pre className="cs-code-block">
                                <code>{e.code}</code>
                              </pre>
                            )
                          ) : e.content_type === "Video-text" ? (
                            <div className="video-container">
                              <video
                                className="video-fluid-desc"
                                src={e.video_link}
                                autoPlay="autoplay"
                                loop="loop"
                              ></video>
                              <div className="video-desc">
                                <h1>{e?.heading_green}</h1>
                                {e?.text_content && (
                                  <p
                                    className="case-study-text"
                                    style={{ color: "#000000b5" }}
                                  >
                                    {e?.text_content}
                                  </p>
                                )}
                              </div>
                            </div>
                          ) : (
                            <video
                              className="img-fluid"
                              src={e.video_link}
                              autoPlay="autoplay"
                              loop="loop"
                            ></video>
                          )}
                        </section>
                      );
                    }

                    return (
                      <React.Fragment key={index}>
                        {shouldInsertMetricsHere && metricsSection}
                        {tileElement}
                      </React.Fragment>
                    );
                  })}

                  {metricsInsertIndex >= renderedBlocks.length &&
                    metricsSection}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="nda-div">
            <span className="grad-overlay-sm-mw">
              <Lottie className="nda-2" animationData={NDA} loop={true} />
              <h2 className="heading-nda">
                {isLoading ? (
                  <span
                    className="skeleton skeleton-text"
                    style={{ width: 160, height: "1em" }}
                  />
                ) : (
                  caseStudy.title
                )}
              </h2>
              <form className="form-signin" onSubmit={login()}>
                <div className="key-div">
                  <p className="text-nda-1">This content is protected!</p>
                  <p className="text-nda-2">Enter the password to view </p>

                  <div className="password-row">
                    <input
                      className="password-input"
                      type="password"
                      placeholder="May the password be with you."
                      required
                    ></input>
                    <input
                      className="arrow"
                      type="image"
                      alt="submit"
                      src={arrow}
                      width={40}
                    ></input>
                  </div>
                </div>
              </form>
            </span>
          </div>
        )}

        <button
          type="button"
          className={`cs-fab cs-fab-top ${showBottomBar ? "visible" : ""}`}
          onClick={scrollTop}
          aria-label="Back to top"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 19V5M12 5L5 12M12 5L19 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {isProtected && showBottomBar && (
          <div className="cs-scroll-progress">
            <div
              className="cs-scroll-progress-track"
              ref={scrollProgressTrackRef}
              onPointerDown={handleScrollTrackPointerDown}
              onPointerMove={handleScrollTrackPointerMove}
              onPointerUp={handleScrollTrackPointerUp}
              onPointerCancel={handleScrollTrackPointerUp}
            >
              {scrollProgressTicks.map((tick) => (
                <span className="cs-scroll-progress-tick" key={tick} />
              ))}
              <span
                className="cs-scroll-progress-thumb"
                style={{ left: `${scrollProgress * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {isProtected ? (
        <div>
          {" "}
          <div>
            <div className="thank-you-div" ref={thankYouRef}>
              <img src={line} alt="" />
              <h1>Thank You for Reading</h1>
              <img src={line} alt="" style={{ rotate: "180deg" }} />
            </div>
          </div>

          {otherCaseStudyPick && (
            <section className="cs-other-case-study">
              <div className="cs-other-heading-column">
                <h1
                  className="stack-heading"
                  style={{
                    fontFamily: "Kvictor",
                    textTransform: "none",
                    fontWeight: 200,
                    fontSize: "clamp(1.2rem, 3vw, 2.5rem)",
                    letterSpacing : "0em"
                  }}
                >
                 Next Case Study
                </h1>

                {allCaseStudiesForDrawer.length > 0 && (
                  <button
                    type="button"
                    className="cs-view-all-cta"
                    onClick={() => setIsCaseStudyDrawerOpen(true)}
                  >
                    View All Case Studies
                    <span className="cs-view-all-cta-arrow" aria-hidden="true">
                      →
                    </span>
                  </button>
                )}
              </div>
              <div className="cs-other-card-column">
                <OtherCaseStudyCard
                  picked={otherCaseStudyPick}
                  galleryImages={otherCaseStudyImages}
                  onOpenMultiModal={() => setMultiModalItem(otherCaseStudyPick)}
                />
              </div>
            </section>
          )}

          <div
            className={`cs-drawer-backdrop ${isCaseStudyDrawerOpen ? "visible" : ""}`}
            onClick={() => setIsCaseStudyDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside
            className={`cs-drawer ${isCaseStudyDrawerOpen ? "open" : ""}`}
            aria-hidden={!isCaseStudyDrawerOpen}
          >
            <div className="cs-drawer-header">
              <h2 className="cs-drawer-heading">All Case Studies</h2>
              <button
                type="button"
                className="multi-case-modal-close"
                onClick={() => setIsCaseStudyDrawerOpen(false)}
              >
                <span className="multi-case-modal-close-dot" />
                Close
              </button>
            </div>
            <div className="cs-drawer-list">
              {allCaseStudiesForDrawer.map((entry) => (
                <CaseStudyDrawerItem
                  key={`${entry.source}-${entry.item.id}`}
                  entry={entry}
                  onOpenMultiModal={(item) => {
                    setMultiModalItem(item);
                    setIsCaseStudyDrawerOpen(false);
                  }}
                  onNavigate={() => setIsCaseStudyDrawerOpen(false)}
                />
              ))}
            </div>
          </aside>

          <Modal
            open={Boolean(multiModalItem)}
            onClose={() => setMultiModalItem(null)}
            center
            showCloseIcon={false}
            blockScroll={false}
            classNames={{
              modal: "multi-case-modal",
              modalAnimationIn: "multi-case-slide-in",
              modalAnimationOut: "multi-case-slide-out",
            }}
          >
            <div className="multi-case-modal-topbar">
              <h2 className="multi-case-modal-heading">
                Choose case study to view
              </h2>
              <button
                type="button"
                className="multi-case-modal-close"
                onClick={() => setMultiModalItem(null)}
              >
                <span className="multi-case-modal-close-dot" />
                Close
              </button>
            </div>
            <div className="multi-case-modal-grid">
              {(() => {
                if (!multiModalItem) return null;
                const { source, item: e } = multiModalItem;
                const isMain = source === "main";
                const multi = isMain
                  ? e?.data?.multi_case_studies?.[0]
                  : e?.data?.multi_case_study?.[0];
                return (
                  <>
                    <MultiCaseOptionCard
                      link={
                        isMain
                          ? multi?.case_study_link_1
                          : multi?.content_relationship_1
                      }
                      fallbackUrl={multi?.link_1}
                      title={multi?.title_1}
                      description={multi?.description_1}
                      image={multi?.image_1}
                      onNavigate={() => setMultiModalItem(null)}
                    />
                    <MultiCaseOptionCard
                      link={
                        isMain
                          ? multi?.case_study_link_2
                          : multi?.content_relationshiop_2
                      }
                      fallbackUrl={multi?.link_2}
                      title={multi?.title_2}
                      description={multi?.description_2}
                      image={multi?.image_2}
                      onNavigate={() => setMultiModalItem(null)}
                    />
                  </>
                );
              })()}
            </div>
          </Modal>

          <section id="contact">
            <Contact />
            <Footer />
          </section>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}
